import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout } from "@/components/LegalLayout";

export const Route = createFileRoute("/legal/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — The Hospitality Reserve" },
      {
        name: "description",
        content:
          "How The Hospitality Reserve collects, uses, and safeguards personal data for bartenders, venues, and visitors to our platform.",
      },
      { property: "og:title", content: "Privacy Policy — The Hospitality Reserve" },
      { property: "og:url", content: "https://thehospitalityreserve.com/legal/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/legal/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="18 June 2026">
      <p>
        This Privacy Notice for The Hospitality Reserve ('<strong>we</strong>', '
        <strong>us</strong>', or '<strong>our</strong>'), describes how and why
        we might access, collect, store, use, and/or share ('
        <strong>process</strong>') your personal information when you use our
        services ('<strong>Services</strong>'), including when you:
      </p>
      <ul>
        <li>
          Visit our website at{" "}
          <a href="https://thehospitalityreserve.com">https://thehospitalityreserve.com</a> or
          any website of ours that links to this Privacy Notice
        </li>
        <li>
          Use The Hospitality Reserve, a software platform connecting bartenders to
          venues
        </li>
        <li>
          Engage with us in other related ways, including any marketing or events
        </li>
      </ul>
      <p>
        <strong>Questions or concerns?</strong> Reading this Privacy Notice
        will help you understand your privacy rights and choices. We are
        responsible for making decisions about how your personal information is
        processed. If you do not agree with our policies and practices, please do
        not use our Services. If you still have any questions or concerns, please
        contact us at{" "}
        <a href="mailto:hello@thehospitalityreserve.com">hello@thehospitalityreserve.com</a>.
      </p>
      <h2>1. What information do we collect?</h2>
      <h3>Personal information you disclose to us</h3>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>We collect personal information that you provide to us.</em> We
        collect personal information that you voluntarily provide to us when you
        register on the Services, express an interest in obtaining information
        about us or our products and Services, when you participate in activities
        on the Services, or otherwise when you contact us.
      </p>
      <p>
        <strong>Personal Information Provided by You.</strong> The personal
        information that we collect depends on the context of your interactions
        with us and the Services, the choices you make, and the products and
        features you use. The personal information we collect may include the
        following:
      </p>
      <ul>
        <li>names</li>
        <li>phone numbers</li>
        <li>email addresses</li>
        <li>job titles</li>
        <li>usernames</li>
        <li>contact preferences</li>
        <li>contact or authentication data</li>
        <li>billing addresses</li>
      </ul>
      <p>
        <strong>Sensitive Information.</strong> When necessary, with your consent
        or as otherwise permitted by applicable law, we process the following
        categories of sensitive information:
      </p>
      <ul>
        <li>financial data</li>
        <li>social security numbers or other government identifiers</li>
      </ul>
      <p>
        <strong>Payment Data.</strong> We may collect data necessary to process
        your payment if you choose to make purchases, such as your payment
        instrument number, and the security code associated with your payment
        instrument. All payment data is handled and stored by Stripe. You may
        find their privacy notice link(s) here:{" "}
        <a href="https://stripe.com/gb/privacy">Stripe privacy notice</a>. All
        personal information that you provide to us must be true, complete, and
        accurate, and you must notify us of any changes to such personal
        information.
      </p>
      <h3>Information automatically collected</h3>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          Some information — such as your Internet Protocol (IP) address
          and/or browser and device characteristics — is collected
          automatically when you visit our Services.
        </em>{" "}
        We automatically collect certain information when you visit, use, or
        navigate the Services. This information does not reveal your specific
        identity (like your name or contact information) but may include device
        and usage information, such as your IP address, browser and device
        characteristics, operating system, language preferences, referring URLs,
        device name, country, location, information about how and when you use
        our Services, and other technical information. This information is
        primarily needed to maintain the security and operation of our Services,
        and for our internal analytics and reporting purposes.
      </p>
      <p>
        Like many businesses, we also collect information through cookies and
        similar technologies. The information we collect includes:
      </p>
      <ul>
        <li>
          <em>Location Data.</em> We collect location data such as information
          about your device's location, which can be either precise or imprecise.
          How much information we collect depends on the type and settings of the
          device you use to access the Services. For example, we may use GPS and
          other technologies to collect geolocation data that tells us your
          current location (based on your IP address). You can opt out of
          allowing us to collect this information either by refusing access to
          the information or by disabling your Location setting on your device.
          However, if you choose to opt out, you may not be able to use certain
          aspects of the Services.
        </li>
      </ul>
      <h2>2. How do we process your information?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          We process your information to provide, improve, and administer our
          Services, communicate with you, for security and fraud prevention, and
          to comply with law. We may also process your information for other
          purposes with your consent.
        </em>
      </p>
      <p>
        <strong>
          We process your personal information for a variety of reasons,
          depending on how you interact with our Services, including:
        </strong>
      </p>
      <ul>
        <li>
          <strong>
            To facilitate account creation and authentication and otherwise
            manage user accounts.
          </strong>{" "}
          We may process your information so you can create and log in to your
          account, as well as keep your account in working order.
        </li>
        <li>
          <strong>To deliver and facilitate delivery of services to the user.</strong>{" "}
          We may process your information to provide you with the requested
          service.
        </li>
        <li>
          <strong>To respond to user inquiries/offer support to users.</strong>{" "}
          We may process your information to respond to your inquiries and solve
          any potential issues you might have with the requested service.
        </li>
        <li>
          <strong>To send administrative information to you.</strong> We may
          process your information to send you details about our products and
          services, changes to our terms and policies, and other similar
          information.
        </li>
        <li>
          <strong>To fulfil and manage your orders.</strong> We may process your
          information to fulfil and manage your orders, payments, returns, and
          exchanges made through the Services.
        </li>
        <li>
          <strong>To enable user-to-user communications.</strong> We may process
          your information if you choose to use any of our offerings that allow
          for communication with another user.
        </li>
        <li>
          <strong>To save or protect an individual's vital interest.</strong>{" "}
          We may process your information when necessary to save or protect an
          individual's vital interest, such as to prevent harm.
        </li>
      </ul>
      <h2>3. What legal bases do we rely on to process your personal information?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          We only process your personal information when we believe it is
          necessary and we have a valid legal reason (i.e. legal basis) to do so
          under applicable law, like with your consent, to comply with laws, to
          provide you with services to enter into or fulfil our contractual
          obligations, to protect your rights, or to fulfil our legitimate
          business interests.
        </em>
      </p>
      <p>
        The General Data Protection Regulation (GDPR) and UK GDPR require us to
        explain the valid legal bases we rely on in order to process your
        personal information. As such, we may rely on the following legal bases
        to process your personal information:
      </p>
      <ul>
        <li>
          <strong>Consent.</strong> We may process your information if you have
          given us permission (i.e. consent) to use your personal information for
          a specific purpose. You can withdraw your consent at any time. Learn
          more about withdrawing your consent.
        </li>
        <li>
          <strong>Performance of a Contract.</strong> We may process your
          personal information when we believe it is necessary to fulfil our
          contractual obligations to you, including providing our Services or at
          your request prior to entering into a contract with you.
        </li>
        <li>
          <strong>Legitimate Interests.</strong> We may process your information
          when we believe it is reasonably necessary to achieve our legitimate
          business interests and those interests do not outweigh your interests
          and fundamental rights and freedoms. For example, we may process your
          personal information for some of the purposes described in order to:
          <ul>
            <li>
              Send users information about special offers and discounts on our
              products and services
            </li>
            <li>Develop and display personalised and relevant advertising</li>
            <li>Support our marketing activities</li>
            <li>
              Diagnose problems and/or prevent fraudulent activities
            </li>
            <li>
              Understand how our users use our products and services so we can
              improve user experience
            </li>
          </ul>
        </li>
        <li>
          <strong>Legal Obligations.</strong> We may process your information
          where we believe it is necessary for compliance with our legal
          obligations, such as to cooperate with a law enforcement body or
          regulatory agency, exercise or defend our legal rights, or disclose
          your information as evidence in litigation in which we are involved.
        </li>
        <li>
          <strong>Vital Interests.</strong> We may process your information
          where we believe it is necessary to protect your vital interests or
          the vital interests of a third party, such as situations involving
          potential threats to the safety of any person.
        </li>
      </ul>
      <p>
        <strong>
          In legal terms, we are generally considered the 'controller' of your
          personal information.
        </strong>
      </p>
      <h2>4. When and with whom do we share your personal information?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          We may share information in specific situations described in this
          section and/or with the following categories of third parties.
        </em>
      </p>
      <p>
        <strong>Vendors, Consultants, and Other Third-Party Service Providers.</strong>{" "}
        We may share your data with third-party vendors, service providers,
        contractors, or agents ('third parties') who perform services for us or
        on our behalf and require access to such information to do that work.
        The categories of third parties we may share personal information with
        are as follows:
      </p>
      <ul>
        <li>Ad Networks</li>
        <li>Affiliate Marketing Programmes</li>
        <li>Cloud Computing Services</li>
        <li>Communication and Collaboration Tools</li>
        <li>Data Analytics Services</li>
        <li>Data Storage Service Providers</li>
        <li>Finance and Accounting Tools</li>
        <li>Government Entities</li>
        <li>Order Fulfilment Service Providers</li>
        <li>Payment Processors</li>
        <li>Product Engineering and Design Tools</li>
        <li>Retargeting Platforms</li>
        <li>Social Networks</li>
        <li>Testing Tools</li>
        <li>User Account Registration and Authentication Services</li>
        <li>Website Hosting Service Providers</li>
      </ul>
      <p>
        We also may need to share your personal information in the following
        situations:
      </p>
      <ul>
        <li>
          <strong>Business Transfers.</strong> We may share or transfer your
          information in connection with, or during negotiations of, any merger,
          sale of company assets, financing, or acquisition of all or a portion
          of our business to another company.
        </li>
        <li>
          <strong>When we use Google Analytics.</strong> We may share your
          information with Google Analytics to track and analyse the use of the
          Services. The Google Analytics Advertising Features that we may use
          include: Google Analytics Demographics and Interests Reporting. To opt
          out of being tracked by Google Analytics across the Services, visit{" "}
          <a href="https://myactivity.google.com/privacyadvisor">https://myactivity.google.com/privacyadvisor</a>.
          For more information on the privacy practices of Google, please visit
          the{" "}
          <a href="https://policies.google.com/privacy">Google Privacy and Terms page</a>.
        </li>
        <li>
          <strong>When we use Google Maps Platform APIs.</strong> We may share
          your information with Google's Maps Platform APIs, including the
          Google Cloud Platform, which is covered by the{" "}
          <a href="https://cloud.google.com/terms/">Google Cloud Platform Terms of Service</a>{" "}
          and the{" "}
          <a href="https://policies.google.com/privacy">Google Privacy Policy</a>.
          We obtain and store on your device ('cache') your location. You may
          revoke your consent at any time by contacting us at the contact details
          provided at the end of this document.
        </li>
        <li>
          <strong>Affiliates.</strong> We may share your information with our
          affiliates, in which case we will require those affiliates to honour
          this Privacy Notice. Affiliates include our parent company and any
          subsidiaries, joint venture partners, or other companies that we
          control or that are under common control with us.
        </li>
        <li>
          <strong>Business Partners.</strong> We may share your information with
          our business partners to offer you certain products, services, or
          promotions.
        </li>
      </ul>
      <h2>5. Do we use cookies and other tracking technologies?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          We may use cookies and other tracking technologies to collect and
          store your information.
        </em>
      </p>
      <p>
        We may use cookies and similar tracking technologies (like web beacons
        and pixels) to access or store information. Specific information about
        how we use such technologies and how you can refuse certain cookies is
        set out in our Cookie Notice.
      </p>
      <h2>6. How long do we keep your information?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          We keep your information for as long as necessary to fulfil the
          purposes outlined in this Privacy Notice unless otherwise required by
          law.
        </em>
      </p>
      <p>
        We will only keep your personal information for as long as it is
        necessary for the purposes set out in this Privacy Notice, unless a
        longer retention period is required or permitted by law (such as tax,
        accounting, or other legal requirements). No purpose in this notice
        will require us keeping your personal information for longer than the
        period of time in which users have an account with us.
      </p>
      <p>
        When we have no ongoing legitimate business need to process your
        personal information, we will either delete or anonymise such
        information, or, if this is not possible (for example, because your
        personal information has been stored in backup archives), then we will
        securely store your personal information and isolate it from any further
        processing until deletion is possible.
      </p>
      <h2>7. Do we collect information from minors?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>We do not knowingly collect data from or market to children under 18 years of age.</em>
      </p>
      <p>
        We do not knowingly collect, solicit data from, or market to children
        under 18 years of age, nor do we knowingly sell such personal
        information. By using the Services, you represent that you are at least
        18 or that you are the parent or guardian of such a minor and consent to
        such minor dependent's use of the Services. If we learn that personal
        information from users less than 18 years of age has been collected, we
        will deactivate the account and take reasonable measures to promptly
        delete such data from our records. If you become aware of any data we may
        have collected from children under age 18, please contact us at{" "}
        <a href="mailto:hello@thehospitalityreserve.com">hello@thehospitalityreserve.com</a>.
      </p>
      <h2>8. What are your privacy rights?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          In some regions, such as the European Economic Area (EEA), United
          Kingdom (UK), and Switzerland, you have rights that allow you greater
          access to and control over your personal information.
        </em>{" "}
        You may review, change, or terminate your account at any time, depending
        on your country, province, or state of residence.
      </p>
      <p>
        In some regions (like the EEA, UK, and Switzerland), you have certain
        rights under applicable data protection laws. These may include the
        right (i) to request access and obtain a copy of your personal
        information, (ii) to request rectification or erasure, (iii) to restrict
        the processing of your personal information, (iv) if applicable, to data
        portability, and (v) not to be subject to automated decision-making. In
        certain circumstances, you may also have the right to object to the
        processing of your personal information. You can make such a request by
        contacting us by using the contact details provided in the section{" "}
        <a href="mailto:hello@thehospitalityreserve.com">
          'How can you contact us about this notice?'
        </a>{" "}
        below.
      </p>
      <p>
        We will consider and act upon any request by applicable data protection
        laws.
      </p>
      <p>
        If you are located in the EEA or UK and you believe we are unlawfully
        processing your personal information, you also have the right to complain
        to your{" "}
        <a href="https://ico.org.uk/make-a-complaint/">
          Member State data protection authority or UK data protection authority
        </a>.
      </p>
      <p>
        <strong>Withdrawing your consent:</strong> If we are relying on your
        consent to process your personal information, which may be express and/or
        implied consent depending on the applicable law, you have the right to
        withdraw your consent at any time. You can withdraw your consent at any
        time by contacting us by using the contact details provided in the
        section{" "}
        <a href="mailto:hello@thehospitalityreserve.com">
          'How can you contact us about this notice?'
        </a>{" "}
        below.
      </p>
      <p>
        However, please note that this will not affect the lawfulness of the
        processing before its withdrawal nor, when applicable law allows, will
        it affect the processing of your personal information conducted in
        reliance on lawful processing grounds other than consent.
      </p>
      <p>
        <strong>Account Information</strong>
      </p>
      <p>
        If you would at any time like to review or change the information in
        your account or terminate your account, you can contact us using the
        contact information provided.
      </p>
      <p>
        Upon your request to terminate your account, we will deactivate or
        delete your account and information from our active databases. However,
        we may retain some information in our files to prevent fraud, troubleshoot
        problems, assist with any investigations, enforce our legal terms and/or
        comply with applicable legal requirements.
      </p>
      <p>
        <strong>Cookies and similar technologies:</strong> Most Web browsers are
        set to accept cookies by default. If you prefer, you can usually choose
        to set your browser to remove cookies and to reject cookies. If you
        choose to remove cookies or reject cookies, this could affect certain
        features or services of our Services. You may also opt out of interest-
        based advertising by advertisers on our Services.
      </p>
      <p>
        If you have questions or comments about your privacy rights, you may
        email us at{" "}
        <a href="mailto:hello@thehospitalityreserve.com">hello@thehospitalityreserve.com</a>.
      </p>
      <h2>9. Controls for do-not-track features</h2>
      <p>
        Most web browsers and some mobile operating systems and mobile
        applications include a Do-Not-Track ('DNT') feature or setting you can
        activate to signal your privacy preference not to have data about your
        online browsing activities monitored and collected. At this stage, no
        uniform technology standard for recognising and implementing DNT signals
        has been finalised. As such, we do not currently respond to DNT browser
        signals or any other mechanism that automatically communicates your
        choice not to be tracked online. If a standard for online tracking is
        adopted that we must follow in the future, we will inform you about
        that practice in a revised version of this Privacy Notice.
      </p>
      <h2>10. Do we make updates to this notice?</h2>
      <p>
        <strong>
          <em>In Short:</em>
        </strong>{" "}
        <em>
          Yes, we will update this notice as necessary to stay compliant with
          relevant laws.
        </em>{" "}
        We may update this Privacy Notice from time to time. The updated version
        will be indicated by an updated 'Revised' date at the top of this Privacy
        Notice. If we make material changes to this Privacy Notice, we may notify
        you either by prominently posting a notice of such changes or by
        directly sending you a notification. We encourage you to review this
        Privacy Notice frequently to be informed of how we are protecting your
        information.
      </p>
      <h2>11. How can you contact us about this notice?</h2>
      <p>
        If you have questions or comments about this notice, please contact our
        Data Protection Officer by email at{" "}
        <a href="mailto:hello@thehospitalityreserve.com">hello@thehospitalityreserve.com</a> or
        by post at:
      </p>
      <p className="not-italic">
        The Hospitality Reserve
        <br />
        Data Protection Officer
        <br />
        16 Trimley Close
        <br />
        Luton
        <br />
        LU4 9HJ, England
      </p>
      <h2>12. How can you review, update, or delete the data we collect from you?</h2>
      <p>
        Based on the applicable laws of your country, you may have the right to
        request access to the personal information we collect from you, details
        about how we have processed it, correct inaccuracies, or delete your
        personal information. You may also have the right to withdraw your
        consent to our processing of your personal information. These rights may
        be limited in some circumstances by applicable law. To request to
        review, update, or delete your personal information, please fill out and
        submit a{" "}
        <a href="mailto:hello@thehospitalityreserve.com">data subject access request</a>.
      </p>
    </LegalLayout>
  );
}
